import React, { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { buildWhatsAppUrl } from "../utils/whatsapp";
import { trackEvent } from "../lib/posthog";
import WhatsAppIcon from "./icons/WhatsAppIcon";

function ChatWidget() {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState("menu");

  const [phoneError, setPhoneError] = useState(false);

  const [leadData, setLeadData] = useState({
    type: "",
    city: "",
    moveIn: "",
    duration: "",
    budget: "",
    name: "",
    phone: "",
    email: ""
  });

  const updateLeadData = (field, value, nextStep) => {
    setLeadData((prev) => ({
      ...prev,
      [field]: value
    }));
    setStep(nextStep);
  };

  const resetChat = () => {
    setStep("menu");
    setLeadData({
      type: "",
      city: "",
      moveIn: "",
      duration: "",
      budget: "",
      name: "",
      phone: "",
      email: ""
    });
  };

const whatsappMessage = encodeURIComponent(
  `Hallo, ich interessiere mich für ein möbliertes Apartment bei FeelAtHomeNow.

Name: ${leadData.name}
Telefon: ${leadData.phone}
Email: ${leadData.email || "nicht angegeben"}

Art: ${leadData.type}
Stadt: ${leadData.city}
Einzug: ${leadData.moveIn}
Dauer: ${leadData.duration}
Budget: ${leadData.budget}`
);

const whatsappUrl = `https://wa.me/41762398070?text=${whatsappMessage}`;

const contactMessage = `Art: ${leadData.type}
Stadt: ${leadData.city}
Einzug: ${leadData.moveIn}
Dauer: ${leadData.duration}
Budget: ${leadData.budget}`;

const contactUrl = `/contact?` + new URLSearchParams({
  name: leadData.name,
  phone: leadData.phone,
  email: leadData.email,
  message: contactMessage,
}).toString();

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-24 right-5 z-50 bg-brand text-ink rounded-full p-4 shadow-lg"
        aria-label={isOpen ? t.chatWidget.closeChat : t.chatWidget.openChat}
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {isOpen && (
        <div className="fixed bottom-40 right-5 w-[340px] bg-surface rounded-xl border border-line shadow-[0_8px_24px_rgba(15,23,42,0.10)] z-50 overflow-hidden">

          <div className="bg-brand text-white p-4">
            <h3 className="font-semibold text-lg">FeelAtHomeNow Chat</h3>
            <p className="text-sm opacity-90">Wie können wir dir helfen?</p>
          </div>

          <div className="p-4 space-y-3">

            {step === "menu" && (
              <>
                <a
                  href={buildWhatsAppUrl({ source: "chat_widget", url: window.location.pathname })}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { source: "chat_widget" })}
                  className="w-full flex items-center gap-2.5 p-3 rounded-xl border text-left font-medium"
                  data-testid="whatsapp-chatwidget"
                >
                  <WhatsAppIcon className="h-5 w-5 shrink-0 text-[#18A957]" />
                  {t.whatsapp.chatOption}
                </a>

                <button
                  onClick={() => setStep("type")}
                  className="w-full text-left p-3 rounded-xl border"
                >
                  {t.whatsapp.funnelOption}
                </button>

                <a
                  href="/contact"
                  className="block w-full text-left p-3 rounded-xl border"
                >
                  Frage stellen
                </a>
              </>
            )}

            {step === "type" && (
              <>
                <button
                  onClick={() => updateLeadData("type","Apartment","city")}
                  className="w-full text-left p-3 rounded-xl border"
                >
                  Apartment finden
                </button>

                <button
                  onClick={() => updateLeadData("type","Co-Living","city")}
                  className="w-full text-left p-3 rounded-xl border"
                >
                  Co-Living finden
                </button>

                <button
                  onClick={() => updateLeadData("type","Unternehmen","city")}
                  className="w-full text-left p-3 rounded-xl border"
                >
                  Für Unternehmen
                </button>
              </>
            )}

            {step === "city" && (
              <>
                <p>In welcher Stadt suchst du?</p>

                <button onClick={() => updateLeadData("city","Zürich","moveIn")} className="w-full text-left p-3 border rounded-xl">Zürich</button>

                <button onClick={() => updateLeadData("city","Basel","moveIn")} className="w-full text-left p-3 border rounded-xl">Basel</button>

                <button onClick={() => updateLeadData("city","Bern","moveIn")} className="w-full text-left p-3 border rounded-xl">Bern</button>

                <button onClick={() => updateLeadData("city","Andere","moveIn")} className="w-full text-left p-3 border rounded-xl">Andere</button>
              </>
            )}

            {step === "moveIn" && (
              <>
                <p>Ab wann suchst du?</p>

                <button onClick={() => updateLeadData("moveIn","Sofort","duration")} className="w-full text-left p-3 border rounded-xl">Sofort</button>

                <button onClick={() => updateLeadData("moveIn","Diesen Monat","duration")} className="w-full text-left p-3 border rounded-xl">Diesen Monat</button>

                <button onClick={() => updateLeadData("moveIn","Nächsten Monat","duration")} className="w-full text-left p-3 border rounded-xl">Nächsten Monat</button>
              </>
            )}

            {step === "duration" && (
              <>
                <p>Wie lange möchtest du bleiben?</p>

                <button onClick={() => updateLeadData("duration","1-2 Monate","budget")} className="w-full text-left p-3 border rounded-xl">1-2 Monate</button>

                <button onClick={() => updateLeadData("duration","3-6 Monate","budget")} className="w-full text-left p-3 border rounded-xl">3-6 Monate</button>

                <button onClick={() => updateLeadData("duration","6-12 Monate","budget")} className="w-full text-left p-3 border rounded-xl">6-12 Monate</button>
              </>
            )}

            {step === "budget" && (
              <>
                <p>Wie hoch ist dein Budget?</p>

                <button onClick={() => updateLeadData("budget","unter 1500","name")} className="w-full text-left p-3 border rounded-xl">unter 1500</button>

                <button onClick={() => updateLeadData("budget","1500-2500","name")} className="w-full text-left p-3 border rounded-xl">1500-2500</button>

                <button onClick={() => updateLeadData("budget","2500+","name")} className="w-full text-left p-3 border rounded-xl">2500+</button>
              </>
            )}

{step === "name" && (
  <>
    <p>Wie heisst du?</p>

    <input
      type="text"
      placeholder="Dein Name"
      value={leadData.name}
      onChange={(e) =>
        setLeadData((prev) => ({ ...prev, name: e.target.value }))
      }
      className="w-full border rounded-xl p-3"
    />

    <button
      onClick={() => {
        if (leadData.name.trim() !== "") setStep("phone");
      }}
      className="w-full text-center p-3 rounded-xl bg-brand text-ink"
    >
      Weiter
    </button>
  </>
)}

{step === "phone" && (
  <>
    <p>Deine Telefonnummer?</p>

    <input
      type="tel"
      placeholder="+41..."
      value={leadData.phone}
      onChange={(e) =>
        setLeadData((prev) => ({ ...prev, phone: e.target.value }))
      }
      className="w-full border rounded-xl p-3"
    />
    {phoneError && (
      <p className="text-red-500 text-sm mt-2">
        Bitte eine gültige Telefonnummer eingeben
      </p>
    )}

    <button
  onClick={() => {
    const cleanPhone = leadData.phone.replace(/\s+/g, "").replace(/[^+\d]/g, "");
  if (cleanPhone.length >= 8) {
    setPhoneError(false);
    setStep("email");
  } else {
    setPhoneError(true);
  }
  }}
      className="w-full text-center p-3 rounded-xl bg-brand text-ink"
    >
      Weiter
    </button>
  </>
)}

{step === "email" && (
  <>
    <p>E-Mail (optional)</p>

    <input
      type="email"
      placeholder="name@email.com"
      value={leadData.email}
      onChange={(e) =>
        setLeadData((prev) => ({ ...prev, email: e.target.value }))
      }
      className="w-full border rounded-xl p-3"
    />

    <button
      onClick={() => setStep("result")}
      className="w-full text-center p-3 rounded-xl bg-brand text-ink"
    >
      Weiter
    </button>
  </>
)}

            {step === "result" && (
              <>
                <p>Danke. Anfrage vorbereitet.</p>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center p-3 rounded-xl bg-green-500 text-white"
                >
                  WhatsApp öffnen
                </a>

                <a
                  href={contactUrl}
                  className="block w-full text-center p-3 border rounded-xl"
                >
                  Formular öffnen
                </a>

                <button
                  onClick={resetChat}
                  className="w-full p-3 text-sm"
                >
                  Neu starten
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
